import React, { useEffect, useRef, useState } from 'react';
import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import { speechProvider } from '../speech/speechProvider';

export interface PixiGameIslandProps {
  gameMode: 'find-letter' | 'missing-syllable' | 'garden';
  targetValue: string;
  choices?: string[];
  onCorrectChoice?: (choice: string) => void;
  onWrongChoice?: (choice: string) => void;
  className?: string;
}

export const PixiGameIsland: React.FC<PixiGameIslandProps> = ({
  gameMode,
  targetValue,
  choices = [],
  onCorrectChoice,
  onWrongChoice,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const appRef = useRef<Application | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    let pixiApp: Application | null = null;

    const initPixi = async () => {
      if (!containerRef.current) return;

      const width = containerRef.current.clientWidth || 360;
      const height = 300;

      pixiApp = new Application();
      await pixiApp.init({
        width,
        height,
        backgroundColor: 0xf0fdf4,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        autoDensity: true,
        antialias: true,
      });

      if (isCancelled || !containerRef.current) {
        pixiApp.destroy(true, { children: true });
        return;
      }

      appRef.current = pixiApp;
      containerRef.current.innerHTML = '';
      containerRef.current.appendChild(pixiApp.canvas);

      // Setup Scene based on gameMode
      setupScene(pixiApp, width, height);
      setIsReady(true);
    };

    initPixi();

    return () => {
      isCancelled = true;
      if (appRef.current) {
        try {
          appRef.current.destroy(true, { children: true });
        } catch (e) {
          console.warn('Pixi destroy cleanup error:', e);
        }
        appRef.current = null;
      }
    };
  }, [gameMode, targetValue, choices]);

  const setupScene = (app: Application, width: number, height: number) => {
    const stage = app.stage;
    stage.removeChildren();

    // Background soft hills / clouds
    const bg = new Graphics();
    // Sky
    bg.rect(0, 0, width, height);
    bg.fill(0xeef7ff);
    // Green grassy hills
    bg.circle(width * 0.25, height + 40, 160);
    bg.fill(0xdcfce7);
    bg.circle(width * 0.75, height + 60, 200);
    bg.fill(0xbbf7d0);
    stage.addChild(bg);

    if (gameMode === 'find-letter') {
      setupFindLetterScene(app, width, height);
    } else if (gameMode === 'garden') {
      setupGardenScene(app, width, height);
    } else {
      setupMissingSyllableScene(app, width, height);
    }
  };

  // --- 1. Find Letter Scene (Bobbing interactive bubble sprites) ---
  const setupFindLetterScene = (app: Application, width: number, height: number) => {
    const stage = app.stage;
    const bubblesContainer = new Container();
    stage.addChild(bubblesContainer);

    const count = choices.length || 3;
    const spacing = width / (count + 1);

    choices.forEach((choice, index) => {
      const bubble = new Container();
      const xPos = spacing * (index + 1);
      const baseY = height * 0.5;

      bubble.x = xPos;
      bubble.y = baseY;

      // Circle graphic
      const circle = new Graphics();
      circle.circle(0, 0, 42);
      circle.fill(0xffffff);
      circle.stroke({ width: 4, color: 0x3b82f6 });
      bubble.addChild(circle);

      // Inner glow
      const inner = new Graphics();
      inner.circle(-12, -12, 12);
      inner.fill({ color: 0xffffff, alpha: 0.6 });
      bubble.addChild(inner);

      // Letter text
      const style = new TextStyle({
        fontFamily: 'Fredoka, Arial, sans-serif',
        fontSize: 40,
        fontWeight: 'bold',
        fill: 0x1e3a8a,
      });
      const text = new Text({ text: choice, style });
      text.anchor.set(0.5);
      bubble.addChild(text);

      // Interactivity
      bubble.eventMode = 'static';
      bubble.cursor = 'pointer';

      let isPopping = false;
      bubble.on('pointerdown', () => {
        if (isPopping) return;

        if (choice === targetValue) {
          isPopping = true;
          speechProvider.playChime('success');
          // Pop animation + particles
          spawnParticles(app, bubble.x, bubble.y, 0x10b981);
          bubble.scale.set(1.2);
          setTimeout(() => {
            bubble.visible = false;
            onCorrectChoice?.(choice);
          }, 150);
        } else {
          speechProvider.playChime('tap');
          // Wobble animation
          wobbleElement(bubble);
          onWrongChoice?.(choice);
        }
      });

      // Floating gentle oscillation ticker
      let elapsed = index * 1.5;
      app.ticker.add((time) => {
        if (isPopping) return;
        elapsed += time.deltaTime * 0.04;
        bubble.y = baseY + Math.sin(elapsed) * 12;
      });

      bubblesContainer.addChild(bubble);
    });
  };

  // --- 2. Missing Syllable Scene ---
  const setupMissingSyllableScene = (app: Application, width: number, height: number) => {
    const stage = app.stage;
    const boxContainer = new Container();
    boxContainer.x = width * 0.5;
    boxContainer.y = height * 0.45;
    stage.addChild(boxContainer);

    // Mystery box graphic
    const box = new Graphics();
    box.roundRect(-60, -50, 120, 100, 20);
    box.fill(0xfef08a);
    box.stroke({ width: 5, color: 0xeab308 });
    boxContainer.addChild(box);

    const questionStyle = new TextStyle({
      fontFamily: 'Fredoka, sans-serif',
      fontSize: 48,
      fontWeight: 'bold',
      fill: 0x854d0e,
    });
    const qText = new Text({ text: '?', style: questionStyle });
    qText.anchor.set(0.5);
    boxContainer.addChild(qText);

    // Continuous pulse
    let scaleElapsed = 0;
    app.ticker.add((time) => {
      scaleElapsed += time.deltaTime * 0.05;
      const s = 1 + Math.sin(scaleElapsed) * 0.06;
      boxContainer.scale.set(s);
    });

    // Choices as interactive stones below
    const count = choices.length;
    const spacing = width / (count + 1);

    choices.forEach((choice, index) => {
      const stone = new Container();
      stone.x = spacing * (index + 1);
      stone.y = height * 0.82;

      const stoneGfx = new Graphics();
      stoneGfx.roundRect(-35, -24, 70, 48, 14);
      stoneGfx.fill(0xffedd5);
      stoneGfx.stroke({ width: 3, color: 0xf97316 });
      stone.addChild(stoneGfx);

      const style = new TextStyle({
        fontFamily: 'Fredoka, sans-serif',
        fontSize: 22,
        fontWeight: 'bold',
        fill: 0x7c2d12,
      });
      const t = new Text({ text: choice, style });
      t.anchor.set(0.5);
      stone.addChild(t);

      stone.eventMode = 'static';
      stone.cursor = 'pointer';

      stone.on('pointerdown', () => {
        if (choice === targetValue) {
          speechProvider.playChime('success');
          spawnParticles(app, stone.x, stone.y, 0xeab308);
          onCorrectChoice?.(choice);
        } else {
          speechProvider.playChime('tap');
          wobbleElement(stone);
          onWrongChoice?.(choice);
        }
      });

      stage.addChild(stone);
    });
  };

  // --- 3. Garden Scene (Swaying flowers & interactive nature) ---
  const setupGardenScene = (app: Application, width: number, height: number) => {
    const stage = app.stage;

    // Stem
    const stem = new Graphics();
    stem.moveTo(width * 0.5, height);
    stem.quadraticCurveTo(width * 0.5 + 10, height * 0.65, width * 0.5, height * 0.45);
    stem.stroke({ width: 8, color: 0x16a34a });
    stage.addChild(stem);

    // Leaves
    const leaf = new Graphics();
    leaf.ellipse(width * 0.5 + 24, height * 0.65, 20, 10);
    leaf.fill(0x22c55e);
    stage.addChild(leaf);

    // Flower blossom head
    const flowerHead = new Container();
    flowerHead.x = width * 0.5;
    flowerHead.y = height * 0.45;

    // 5 Petals
    for (let i = 0; i < 5; i++) {
      const angle = (i * Math.PI * 2) / 5;
      const petal = new Graphics();
      petal.circle(Math.cos(angle) * 28, Math.sin(angle) * 28, 20);
      petal.fill(0xf472b6);
      flowerHead.addChild(petal);
    }
    // Flower center
    const center = new Graphics();
    center.circle(0, 0, 16);
    center.fill(0xfacc15);
    flowerHead.addChild(center);

    stage.addChild(flowerHead);

    flowerHead.eventMode = 'static';
    flowerHead.cursor = 'pointer';
    flowerHead.on('pointerdown', () => {
      speechProvider.playChime('water');
      spawnParticles(app, flowerHead.x, flowerHead.y, 0xf472b6);
    });

    // Gentle swaying
    let sway = 0;
    app.ticker.add((time) => {
      sway += time.deltaTime * 0.03;
      flowerHead.rotation = Math.sin(sway) * 0.12;
      flowerHead.x = width * 0.5 + Math.sin(sway) * 6;
    });
  };

  // --- Helper: Spawn celebration particles in Pixi ---
  const spawnParticles = (app: Application, x: number, y: number, color: number) => {
    const particleContainer = new Container();
    app.stage.addChild(particleContainer);

    for (let i = 0; i < 16; i++) {
      const p = new Graphics();
      p.circle(0, 0, Math.random() * 4 + 3);
      p.fill(color);
      p.x = x;
      p.y = y;

      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 5 + 2;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;

      particleContainer.addChild(p);

      let life = 1;
      const tickerFunc = (time: { deltaTime: number }) => {
        p.x += vx * time.deltaTime;
        p.y += vy * time.deltaTime;
        life -= 0.04 * time.deltaTime;
        p.alpha = life;

        if (life <= 0) {
          app.ticker.remove(tickerFunc);
          p.destroy();
        }
      };
      app.ticker.add(tickerFunc);
    }

    setTimeout(() => {
      particleContainer.destroy({ children: true });
    }, 1000);
  };

  // --- Helper: Gentle wobble for wrong choices ---
  const wobbleElement = (element: Container) => {
    const origX = element.x;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      element.x = origX + (step % 2 === 0 ? 6 : -6);
      if (step > 6) {
        clearInterval(interval);
        element.x = origX;
      }
    }, 40);
  };

  return (
    <div className={`relative w-full rounded-3xl overflow-hidden shadow-inner border-2 border-emerald-200 ${className}`}>
      {/* Pixi Canvas Mounting Container */}
      <div ref={containerRef} className="w-full flex items-center justify-center min-h-[300px]" />

      {/* Engine Badge */}
      <div className="absolute top-2.5 right-3 bg-white/80 backdrop-blur-xs text-[10px] font-bold text-emerald-800 px-2.5 py-1 rounded-full border border-emerald-200 pointer-events-none shadow-2xs flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
        <span>PixiJS v8 2D Engine</span>
      </div>
    </div>
  );
};
